/*!
 * EGT Media Manager (element) — renders the TEST-MODE build surface that lists every
 * image/video used across the EGT pages, BY PAGE -> SECTION, each with a PREVIEW,
 * where-it's-used, and a REPLACE control (upload a file OR point to a server path).
 *
 * This is a TOOL surface (test-mode-only build helper; the route 404s in
 * production) — on-brand using the shared EGT
 * chrome (S.page nav/footer + frosted glass), but optimized for utility (dense,
 * scannable, fast) rather than a marketing hero. It composes pure UA/JSON; all
 * replace behavior is wired client-side by app/public/src/egt.media-manager.js
 * (keyed off the data-attributes emitted here).
 *
 * NOTE on form controls: the platform's uae / ua/interface controls + UATools
 * save/load are schema-document controls (a wizard binds one field schema to one
 * document). This surface needs N independent inline replace controls, each
 * targeting a COMPUTED public slot path — there is no single document to bind. So
 * each slot uses a native <input type="file"> + a native path <input>, posted as
 * an isolated request to /studio/media/replace by the client. This is the honest
 * fit; a wizard control does not model "swap the file at this computed path."
 */

/* NOTE: the namespace is dot/letter only (no hyphen). The client element-registry
 * generator emits `$.<namespace> = require(...)` using dot notation, so a hyphen
 * ("egt.media-manager") would produce invalid JS ("$.egt.media-manager =") and
 * break the webpack client bundle. This element is server-rendered (required
 * directly by the route), so the exact namespace token is not otherwise load-
 * bearing — keep it hyphen-free. */
var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.mediamanager",
  title: "EGT Media Manager",
  description: "Test-mode build surface to inventory + replace the media used across the EGT surfaces.",
  supportServer: true
});

var S = require("../helpers/egt.sections.js");

/* page-key -> friendly label, populated from the inventory in render() so the
 * shared-asset badge can name the surfaces an asset is reused on. */
var PAGE_LABELS = {};

/* SHARED-ASSET badge — the whole point of the "don't reuse across surfaces
 * unless it's a logo" rule. An asset referenced by more than one page is flagged
 * here so a replace is never done blind: swapping a shared file changes EVERY
 * surface that points at it. Logos/icons (.svg) are the sanctioned exception and
 * read as neutral, not a warning. */
function sharedBadge(item) {
  var keys = item.pages ? Object.keys(item.pages) : [];
  if (keys.length <= 1) { return null; }
  var isLogo = /\.svg$/i.test(item.path);
  var labels = keys.map(function (k) { return PAGE_LABELS[k] || k; });
  return {
    n: "div", c: "egt-mm__shared " + (isLogo ? "egt-mm__shared--ok" : "egt-mm__shared--warn"),
    i: [
      icon(isLogo ? "link" : "warn"),
      { n: "span", c: "egt-mm__shared-lead",
        i: isLogo
          ? ("Shared logo · " + keys.length + " surfaces")
          : ("Shared across " + keys.length + " surfaces — replacing changes all of them") },
      { n: "span", c: "egt-mm__shared-list", i: labels.join("  ·  ") }
    ]
  };
}

/* a small inline icon set (stroked, currentColor) so the tool reads on-brand
 * without new assets. */
function icon(kind) {
  var base = { n: "svg", c: "egt-mm__ic", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor",
    "stroke-width": "1.8", "stroke-linecap": "round", "stroke-linejoin": "round", "aria-hidden": "true", focusable: "false" };
  var paths = {
    upload: [ { n: "path", d: "M12 16V4" }, { n: "path", d: "M7 9l5-5 5 5" }, { n: "path", d: "M5 20h14" } ],
    link: [ { n: "path", d: "M10 13a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1" }, { n: "path", d: "M14 11a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1" } ],
    image: [ { n: "rect", x: "3", y: "4", width: "18", height: "16", rx: "2" }, { n: "circle", cx: "8.5", cy: "9.5", r: "1.5" }, { n: "path", d: "M21 16l-5-5L5 20" } ],
    video: [ { n: "rect", x: "3", y: "5", width: "18", height: "14", rx: "2" }, { n: "path", d: "M10 9l5 3-5 3z" } ],
    warn: [ { n: "path", d: "M12 9v4" }, { n: "path", d: "M12 17h.01" }, { n: "path", d: "M10.3 3.9l-8 14A2 2 0 004 21h16a2 2 0 001.7-3.1l-8-14a2 2 0 00-3.4 0z" } ],
    view: [ { n: "path", d: "M14 4h6v6" }, { n: "path", d: "M20 4l-8 8" }, { n: "path", d: "M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5" } ],
    search: [ { n: "circle", cx: "11", cy: "11", r: "7" }, { n: "path", d: "M21 21l-4.3-4.3" } ]
  };
  base.i = paths[kind] || paths.image;
  return base;
}

/* VIEW-ON-SITE control — the live page(s) where this asset actually shows, so a
 * founder can open it and go "oh, that's what I'm changing". One place -> a direct
 * "View on site" link; more than one -> a small menu of the surfaces (each a
 * deep link to that page's URL). Built from the refs' resolved slugs (deduped);
 * assets that only live in slug-less shared helpers get no link. */
function viewControl(item) {
  var seen = {};
  var targets = [];
  (item.refs || []).forEach(function (r) {
    var slug = r.slug;
    if (!slug || seen[slug]) { return; }
    seen[slug] = true;
    targets.push({ slug: slug, label: PAGE_LABELS[r.page] || r.page || slug });
  });
  if (!targets.length) { return null; }
  if (targets.length === 1) {
    return {
      n: "a", c: "egt-mm__btn egt-mm__btn--view", href: targets[0].slug, target: "_blank", rel: "noopener",
      i: [ icon("view"), { n: "span", i: "View on site" } ]
    };
  }
  return {
    n: "details", c: "egt-mm__view",
    i: [
      { n: "summary", c: "egt-mm__btn egt-mm__btn--view",
        i: [ icon("view"), { n: "span", i: "View on site (" + targets.length + ")" } ] },
      { n: "div", c: "egt-mm__view-list",
        i: targets.map(function (t) {
          return { n: "a", c: "egt-mm__view-link", href: t.slug, target: "_blank", rel: "noopener",
            i: [ { n: "span", c: "egt-mm__view-label", i: t.label }, { n: "span", c: "egt-mm__view-slug", i: t.slug } ] };
        }) }
    ]
  };
}

function fmtBytes(n) {
  if (!n && n !== 0) { return ""; }
  if (n < 1024) { return n + " B"; }
  if (n < 1024 * 1024) { return (n / 1024).toFixed(0) + " KB"; }
  return (n / (1024 * 1024)).toFixed(1) + " MB";
}

/* APPROVE toggle — a personal "I've done / approved this image change" marker.
 * Purely for the founder's own tracking; state lives in the browser (localStorage,
 * wired client-side), keyed by the asset path, so it persists across reloads and
 * server restarts and marks every instance of the same image at once. Checking it
 * highlights the whole card; unchecking clears it. */
function approveControl(item) {
  return {
    n: "label", c: "egt-mm__approve", "data-egt-mm-approve": item.path,
    title: "Mark this image change approved / done (your own tracking; saved in this browser)",
    i: [
      { n: "input", type: "checkbox", c: "egt-mm__approve-cb", "data-egt-mm-approve-cb": "true" },
      { n: "span", c: "egt-mm__approve-txt", i: "Done" }
    ]
  };
}

/* PREVIEW — image thumbnail, or a video. A video with a captured/uploaded
 * poster shows the poster (cheap, instant); one without falls back to a
 * muted inline <video> so something still previews. */
function preview(item) {
  if (item.kind === "video") {
    if (item.poster) {
      return { n: "div", c: "egt-mm__prev egt-mm__prev--video",
        i: { n: "img", c: "egt-mm__media", src: item.poster, alt: "", loading: "lazy", decoding: "async" } };
    }
    return { n: "div", c: "egt-mm__prev egt-mm__prev--video",
      i: { n: "video", c: "egt-mm__media", src: item.path, muted: true, loop: true,
        playsinline: true, preload: "metadata", "aria-hidden": "true" } };
  }
  return { n: "div", c: "egt-mm__prev",
    i: { n: "img", c: "egt-mm__media", src: item.path, alt: "", loading: "lazy", decoding: "async" } };
}

/* WHERE-USED — the source files + lines this asset appears in. */
function refsBlock(item) {
  var rows = item.refs.map(function (r) {
    return { n: "li", c: "egt-mm__ref", i: [
      { n: "span", c: "egt-mm__ref-file", i: r.file },
      { n: "span", c: "egt-mm__ref-line", i: ":" + r.line }
    ] };
  });
  return { n: "details", c: "egt-mm__uses",
    i: [
      { n: "summary", c: "egt-mm__uses-sum", i: "Used in " + item.refs.length + (item.refs.length === 1 ? " place" : " places") },
      { n: "ul", c: "egt-mm__ref-list", i: rows }
    ] };
}

/* REPLACE control — per slot. Two ways in: upload a file from disk, or pick a
 * file the server already has (a proper on-brand gallery, wired client-side —
 * no paste-a-path field). Raster slots accept any image (the server re-encodes to
 * the slot's format + an AVIF sibling); svg/video keep their exact type. Video
 * slots also get a THUMBNAIL control (capture a frame or upload a poster image) —
 * once set, it's used automatically anywhere this video renders (egt.home.js
 * reads it via inventory.posterFor()). The row's data-* carry the slot, its
 * extension, and kind for the client. */
function replaceControl(item) {
  var inputId = "mmfile-" + item.path.replace(/[^a-z0-9]/gi, "-");
  var isRaster = item.kind === "image" && item.ext !== "svg";
  var uploadLabel = isRaster ? "Upload image" : "Upload ." + item.ext;
  var kids = [
    { n: "div", c: "egt-mm__field",
      i: [
        { n: "label", c: "egt-mm__btn egt-mm__btn--file", for: inputId,
          i: [ icon("upload"), { n: "span", i: uploadLabel } ] },
        { n: "input", id: inputId, type: "file", c: "egt-mm__file", "data-egt-mm-file": "true",
          accept: item.kind === "video" ? "video/*" : "image/*" }
      ] },
    { n: "button", type: "button", c: "egt-mm__btn egt-mm__btn--pick", "data-egt-mm-pick": "true",
      i: [ icon("image"), { n: "span", i: "Pick from server" } ] }
  ];
  if (item.kind === "video") {
    kids.push({ n: "button", type: "button", c: "egt-mm__btn egt-mm__btn--poster", "data-egt-mm-poster": "true",
      i: [ icon("video"), { n: "span", i: "Set thumbnail" } ] });
  }
  kids.push({ n: "p", c: "egt-mm__status", "data-egt-mm-status": "true", role: "status", "aria-live": "polite", i: "" });
  return {
    n: "div", c: "egt-mm__replace", "data-egt-mm-row": "true",
    "data-egt-mm-slot": item.path, "data-egt-mm-ext": item.ext, "data-egt-mm-kind": item.kind,
    i: kids
  };
}

/* the trailing path segment (filename) lower-cased — the client filters/searches
 * on this without re-parsing the path each keystroke. */
function fileNameOf(p) {
  var s = String(p || "");
  var q = s.split("?")[0];
  var seg = q.substring(q.lastIndexOf("/") + 1);
  return seg.toLowerCase();
}

function mediaRow(item, pageKey) {
  var meta = [ { n: "span", c: "egt-mm__kind", i: [ icon(item.kind), item.kind ] } ];
  if (item.exists) {
    meta.push({ n: "span", c: "egt-mm__bytes", i: fmtBytes(item.bytes) });
  } else {
    meta.push({ n: "span", c: "egt-mm__missing", i: [ icon("warn"), "missing on disk" ] });
  }
  return {
    n: "article", c: "egt-mm__item" + (item.exists ? "" : " egt-mm__item--missing"),
    /* filter/search hooks — the client shows/hides on these (kind · page · name). */
    "data-mm-item": "true",
    "data-mm-kind": item.kind,
    "data-mm-page": pageKey || "",
    "data-mm-name": fileNameOf(item.path),
    "data-mm-path": String(item.path || "").toLowerCase(),
    i: [
      preview(item),
      { n: "div", c: "egt-mm__body",
        i: [
          { n: "div", c: "egt-mm__head",
            i: [
              { n: "div", c: "egt-mm__head-main",
                i: [
                  { n: "code", c: "egt-mm__path-label", i: item.path },
                  { n: "div", c: "egt-mm__meta", i: meta }
                ] },
              approveControl(item)
            ] },
          viewControl(item),
          sharedBadge(item),
          refsBlock(item),
          replaceControl(item)
        ].filter(Boolean) }
    ]
  };
}

function sectionBlock(section, pageKey) {
  return {
    n: "section", c: "egt-mm__section", "data-mm-section": "true",
    i: [
      { n: "h3", c: "egt-mm__section-h",
        i: [ { n: "span", c: "egt-mm__section-name", i: prettySection(section.name) },
          { n: "span", c: "egt-mm__section-count", i: String(section.items.length) } ] },
      { n: "div", c: "egt-mm__grid",
        i: section.items.map(function (it) { return mediaRow(it, pageKey); }) }
    ]
  };
}

/* render a section identifier (a render-fn name or banner token) into a friendly
 * label: renderHero -> "Hero", page-meta -> "Page meta", etc. */
function prettySection(name) {
  var s = String(name).replace(/^render/, "").replace(/[-_]+/g, " ").trim();
  s = s.replace(/([a-z])([A-Z])/g, "$1 $2");
  if (!s) { s = "Page"; }
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function pageBlock(page) {
  var count = page.sections.reduce(function (a, s) { return a + s.items.length; }, 0);
  var slugLine = page.slugs && page.slugs.length
    ? { n: "span", c: "egt-mm__page-slugs", i: page.slugs.join("  ·  ") } : null;
  var head = [ { n: "h2", c: "egt-mm__page-h", i: page.label },
    { n: "span", c: "egt-mm__page-count", i: count + " assets" } ];
  if (slugLine) { head.push(slugLine); }
  return {
    n: "section", c: "egt-mm__page", id: "mm-" + page.key, "data-mm-page-block": page.key,
    i: [
      { n: "div", c: "egt-mm__page-head", i: head },
      { n: "div", c: "egt-mm__sections",
        i: page.sections.map(function (s) { return sectionBlock(s, page.key); }) }
    ]
  };
}

/* a sticky in-page jump nav of the pages (utility, not the marketing nav). */
function jumpNav(inv) {
  return {
    n: "nav", c: "egt-mm__jump", "aria-label": "Jump to page",
    i: inv.pages.map(function (p) {
      var count = p.sections.reduce(function (a, s) { return a + s.items.length; }, 0);
      return { n: "a", c: "egt-mm__jump-link", href: "#mm-" + p.key,
        i: [ { n: "span", i: p.label }, { n: "span", c: "egt-mm__jump-n", i: String(count) } ] };
    })
  };
}

/* count assets reused across more than one surface, excluding logos/icons (.svg,
 * the sanctioned exception) — the ones that violate "unique imagery per surface". */
function sharedCount(inv) {
  var pageSet = {};
  (inv.pages || []).forEach(function (p) {
    p.sections.forEach(function (s) {
      s.items.forEach(function (it) {
        if (/\.svg$/i.test(it.path)) { return; }
        (pageSet[it.path] = pageSet[it.path] || {})[p.key] = true;
      });
    });
  });
  return Object.keys(pageSet).filter(function (k) { return Object.keys(pageSet[k]).length > 1; }).length;
}

function hero(inv) {
  var t = inv.totals;
  var shared = sharedCount(inv);
  var stats = { n: "div", c: "egt-mm__stats",
    i: [
      { n: "div", c: "egt-mm__stat", i: [ { n: "span", c: "egt-mm__stat-n", i: String(t.items) }, { n: "span", c: "egt-mm__stat-l", i: "assets" } ] },
      { n: "div", c: "egt-mm__stat", i: [ { n: "span", c: "egt-mm__stat-n", i: String(inv.pages.length) }, { n: "span", c: "egt-mm__stat-l", i: "pages" } ] },
      { n: "div", c: "egt-mm__stat" + (shared ? " egt-mm__stat--warn" : ""),
        i: [ { n: "span", c: "egt-mm__stat-n", i: String(shared) }, { n: "span", c: "egt-mm__stat-l", i: "shared" } ] },
      { n: "div", c: "egt-mm__stat" + (t.missing ? " egt-mm__stat--warn" : ""),
        i: [ { n: "span", c: "egt-mm__stat-n", i: String(t.missing) }, { n: "span", c: "egt-mm__stat-l", i: "missing" } ] },
      /* live "approved" tally — filled by the client from localStorage (your own marks). */
      { n: "div", c: "egt-mm__stat egt-mm__stat--approved",
        i: [ { n: "span", c: "egt-mm__stat-n", "data-egt-mm-approved-count": "true", i: "0" }, { n: "span", c: "egt-mm__stat-l", i: "done" } ] }
    ] };
  var card = { n: "div", c: "egt-glass egt-mm__hero-card",
    i: [
      { n: "p", c: "egt__eyebrow", i: "Studio · Internal" },
      { n: "h1", c: "egt-mm__title", i: [ "Media ", { n: "span", c: "egt__gold", i: "Manager" } ] },
      { n: "p", c: "egt-mm__lead",
        i: "Every image and video the EGT surfaces use, by page and section. Drop in a new file — or point to one on disk — and it replaces the asset in place. A gold “Shared across N surfaces” flag marks any asset reused on more than one page (logos excepted): replacing it changes every one of those surfaces, so a background meant to be unique needs its own file, not a shared swap." },
      stats
    ] };
  return { n: "section", c: "egt-section egt-mm__hero",
    i: { n: "div", c: "egt__shell", i: card } };
}
/* one toggle chip. `pressed` marks the current selection; the client toggles
 * aria-pressed and re-filters. */
function filterChip(filter, value, label, count, pressed) {
  return {
    n: "button", type: "button", c: "egt-mm__chip",
    "data-mm-filter": filter, "data-mm-value": value,
    "aria-pressed": pressed ? "true" : "false",
    i: [
      { n: "span", c: "egt-mm__chip-l", i: label },
      (count != null ? { n: "span", c: "egt-mm__chip-n", i: String(count) } : null)
    ].filter(Boolean)
  };
}

/* the filter/search toolbar: Type chips (All · Photos · Videos), Page chips
 * (All + one per page, multi-select) and a filename search. Purely a hydration
 * host — the client (app/public/src/egt.media-manager.js) does the filtering. */
function controlsBar(inv) {
  var pages = inv.pages || [];
  var nImg = 0, nVid = 0;
  pages.forEach(function (p) {
    p.sections.forEach(function (s) {
      s.items.forEach(function (it) { if (it.kind === "video") { nVid++; } else { nImg++; } });
    });
  });
  var total = nImg + nVid;

  var typeChips = {
    n: "div", c: "egt-mm__chip-row", "data-mm-group": "type",
    i: [
      filterChip("type", "all", "All", total, true),
      filterChip("type", "image", "Photos", nImg, false),
      filterChip("type", "video", "Videos", nVid, false)
    ]
  };

  var pageKids = [ filterChip("page", "all", "All pages", null, true) ];
  pages.forEach(function (p) {
    var c = p.sections.reduce(function (a, s) { return a + s.items.length; }, 0);
    pageKids.push(filterChip("page", p.key, p.label, c, false));
  });
  var pageChips = { n: "div", c: "egt-mm__chip-row", "data-mm-group": "page", i: pageKids };

  var search = {
    n: "div", c: "egt-mm__search",
    i: [
      icon("search"),
      { n: "input", type: "search", c: "egt-mm__search-in", "data-mm-search": "true",
        placeholder: "Search filenames…", "aria-label": "Search media filenames", autocomplete: "off" }
    ]
  };

  return {
    n: "div", c: "egt-mm__controls", "data-mm-controls": "true",
    i: [
      { n: "div", c: "egt-mm__controls-row",
        i: [ { n: "span", c: "egt-mm__controls-k", i: "Type" }, typeChips, search ] },
      { n: "div", c: "egt-mm__controls-row",
        i: [ { n: "span", c: "egt-mm__controls-k", i: "Page" }, pageChips ] },
      { n: "div", c: "egt-mm__filter-status", "data-mm-status": "true",
        i: total + " of " + total + " shown" },
      { n: "p", c: "egt-mm__filter-empty", "data-mm-empty": "true", hidden: true,
        i: "No media matches these filters." }
    ]
  };
}

/**
 * Renders the media-manager page as UA/JSON.
 * @param {object} options { inventory } — the prebuilt inventory tree.
 */
exports.render = function render(options) {
  var inv = (options && options.inventory) || { pages: [], totals: { items: 0, missing: 0 } };
  /* map page keys -> labels so the shared-asset badge can name surfaces. */
  PAGE_LABELS = {};
  (inv.pages || []).forEach(function (p) { PAGE_LABELS[p.key] = p.label; });
  var content = { n: "div", c: "egt-mm",
    i: [
      hero(inv),
      { n: "div", c: "egt__shell egt-mm__layout",
        i: [
          jumpNav(inv),
          { n: "div", c: "egt-mm__pages",
            i: [ controlsBar(inv) ].concat(inv.pages.map(pageBlock)) }
        ] }
    ] };
  return S.page("/reserve", [ content ]);
};
